import axios from 'axios'
import { oracleServiceAxios } from 'setup/SetupAxios'

/**
 * Typed client for every ds-gmeet HTTP route. No other module in Gluon may import
 * oracleServiceAxios for ds-gmeet purposes — go through this file.
 */

// Surface the server's { message } field instead of axios's generic status string.
oracleServiceAxios.interceptors.response.use(undefined, (err: unknown) => {
    if (axios.isAxiosError(err)) {
        const serverMsg = (err.response?.data as { message?: string } | undefined)?.message
        if (serverMsg) return Promise.reject(new Error(serverMsg))
    }
    return Promise.reject(err)
})

export type BotTriggerSource = 'MANUAL' | 'CALENDAR'

export type BotStatus = 'CREATED' | 'RECORDING' | 'DONE' | 'CANCELLED' | 'PENDING' | 'PROCESSED' | 'UNATTRIBUTED' | 'FAILED'

export type BotAckOutcome = 'processed' | 'unattributed' | 'failed'

export type CalendarPlatform = 'google_calendar' | 'microsoft_outlook'

export type CalendarSkipReason = 'NO_VIDEO_URL' | 'NO_EXTERNAL' | 'NOT_SALES' | 'OPT_OUT' | 'PAST' | 'MANUAL_OVERRIDE'

export type RepCalendarStatus = 'CONNECTED' | 'DISCONNECTED' | 'REVOKED'

export type MasterLang = 'uz' | 'ru' | 'en' | 'unknown'

export interface FirestoreTimestampJson {
    _seconds: number
    _nanoseconds: number
}

export interface DsGmeetBot {
    recall_bot_id: string
    user_id: string
    meeting_url: string
    trigger_source: BotTriggerSource
    recall_calendar_event_id: string | null
    status: BotStatus
    gcs_audio_uri: string | null
    recall_recording_id?: string | null
    recall_transcript_id?: string | null
    recall_media_deleted_at: FirestoreTimestampJson | null
    ack_outcome: BotAckOutcome | null
    ack_error: string | null
    acked_at: FirestoreTimestampJson | null
    requeue_log: Array<{ at: FirestoreTimestampJson; by_user_id: string; from: BotStatus }>
    created_at: FirestoreTimestampJson
    updated_at: FirestoreTimestampJson
}

export interface DsGmeetRepCalendar {
    user_id: string
    recall_calendar_id: string
    platform: CalendarPlatform
    account_email: string
    status: RepCalendarStatus
    connected_at: FirestoreTimestampJson
    disconnected_at: FirestoreTimestampJson | null
}

export interface DsGmeetCalendarEvent {
    recall_calendar_event_id: string
    recall_calendar_id: string
    user_id: string
    title: string | null
    meeting_url: string | null
    ical_uid: string | null
    start_time: FirestoreTimestampJson | null
    end_time: FirestoreTimestampJson | null
    attendees: Array<{ email: string; name?: string; response_status?: string; is_organizer?: boolean }>
    qualifies: boolean
    skip_reason: CalendarSkipReason | null
    scheduled_recall_bot_id: string | null
    last_synced_at: FirestoreTimestampJson
}

export interface DsGmeetJob {
    recall_bot_id: string
    event_type: string
    status: 'PENDING' | 'COMPLETED' | 'FAILED'
    retry_count: number
    payload: unknown
    created_at: FirestoreTimestampJson
    updated_at: FirestoreTimestampJson
}

export interface MasterTranscriptSegment {
    speaker: string
    text: string
    lang: MasterLang
    start_ms: number
    end_ms: number
}

export interface CostRollup {
    hoursThisMonth: number
    estimatedRecallSpendUsd: number
    gcsStorageGb: number
}

interface Envelope<T> {
    success: boolean
    data: T
    message?: string
}

/** Convert a Firestore Timestamp-as-JSON to a Date. */
export function timestampToDate(ts: FirestoreTimestampJson | null | undefined): Date | null {
    if (!ts) return null
    return new Date(ts._seconds * 1000 + ts._nanoseconds / 1e6)
}

// ─── Bots / Calls ───────────────────────────────────────────────────────────

export async function startBot(args: { meetingUrl: string; userId: string }): Promise<{ botId: string }> {
    const { data } = await oracleServiceAxios.post<Envelope<{ botId: string }>>('/api/bots/start', {
        meetingUrl: args.meetingUrl,
        user_id: args.userId,
    })
    return data.data
}

async function listCalls(params?: { userId?: string; limit?: number }): Promise<DsGmeetBot[]> {
    const { data } = await oracleServiceAxios.get<Envelope<{ items: DsGmeetBot[] }>>('/api/calls', {
        params: { user_id: params?.userId, limit: params?.limit },
    })
    return data.data.items
}

export async function getCall(botId: string): Promise<{ bot: DsGmeetBot; activity: DsGmeetJob[] }> {
    const { data } = await oracleServiceAxios.get<Envelope<{ bot: DsGmeetBot; activity: DsGmeetJob[] }>>(`/api/calls/${botId}`)
    return data.data
}

export async function getCallAudioUrl(botId: string): Promise<string> {
    const { data } = await oracleServiceAxios.get<Envelope<{ url: string }>>(`/api/calls/${botId}/audio-url`)
    return data.data.url
}

export async function getCallTranscript(botId: string): Promise<MasterTranscriptSegment[]> {
    const { data } = await oracleServiceAxios.get<Envelope<{ segments: MasterTranscriptSegment[] }>>(`/api/calls/${botId}/transcript`)
    return data.data.segments
}

export async function stopCall(botId: string): Promise<void> {
    await oracleServiceAxios.post(`/api/calls/${botId}/stop`)
}

export async function requeueCall(botId: string, userId: string): Promise<void> {
    await oracleServiceAxios.post(`/api/calls/${botId}/requeue`, { user_id: userId })
}

export async function skipCall(botId: string): Promise<void> {
    await oracleServiceAxios.post(`/api/calls/${botId}/skip`, {})
}

export async function listUpcoming(userId: string): Promise<DsGmeetCalendarEvent[]> {
    const { data } = await oracleServiceAxios.get<Envelope<{ items: DsGmeetCalendarEvent[] }>>('/api/calls/upcoming', {
        params: { user_id: userId },
    })
    return data.data.items
}

export async function deleteCalendarEvent(eventId: string): Promise<void> {
    await oracleServiceAxios.delete(`/api/calls/calendar-events/${eventId}`)
}

// ─── Calendar OAuth ─────────────────────────────────────────────────────────

export function calendarConnectUrl(platform: CalendarPlatform, userId: string): string {
    const base = oracleServiceAxios.defaults.baseURL ?? ''
    const url = new URL(`${base}/api/calendar/connect`)
    url.searchParams.set('provider', platform)
    url.searchParams.set('user_id', userId)
    return url.toString()
}

export async function getCalendarStatus(userId: string): Promise<DsGmeetRepCalendar[]> {
    const { data } = await oracleServiceAxios.get<Envelope<{ items: DsGmeetRepCalendar[] }>>('/api/calendar/status', {
        params: { user_id: userId },
    })
    return data.data.items
}

export async function disconnectCalendar(recallCalendarId: string): Promise<void> {
    await oracleServiceAxios.delete('/api/calendar/disconnect', { params: { recall_calendar_id: recallCalendarId } })
}

export async function syncCalendar(recallCalendarId: string): Promise<{ fetched: number; synced: number }> {
    const { data } = await oracleServiceAxios.post<Envelope<{ fetched: number; synced: number }>>('/api/calendar/sync', null, {
        params: { recall_calendar_id: recallCalendarId },
    })
    return data.data
}

// ─── Admin ──────────────────────────────────────────────────────────────────

export async function adminQueue(params?: { status?: BotStatus[]; triggerSource?: BotTriggerSource; limit?: number }): Promise<DsGmeetBot[]> {
    const { data } = await oracleServiceAxios.get<Envelope<{ items: DsGmeetBot[] }>>('/api/admin/ds-gmeet/queue', {
        params: {
            status: params?.status?.join(','),
            trigger_source: params?.triggerSource,
            limit: params?.limit,
        },
    })
    return data.data.items
}

export async function adminCalendars(): Promise<DsGmeetRepCalendar[]> {
    const { data } = await oracleServiceAxios.get<Envelope<{ items: DsGmeetRepCalendar[] }>>('/api/admin/ds-gmeet/calendars')
    return data.data.items
}

export async function adminCost(): Promise<CostRollup> {
    const { data } = await oracleServiceAxios.get<Envelope<CostRollup>>('/api/admin/ds-gmeet/cost')
    return data.data
}

export async function forceCleanup(botId: string): Promise<void> {
    await oracleServiceAxios.post(`/api/admin/ds-gmeet/force-cleanup/${botId}`)
}

// ─── Workspace config ────────────────────────────────────────────────────────

export interface DsGmeetWorkspaceConfig {
    internalEmailDomains: string[]
    skipTitleMarkers: string[]
    botRetentionHours: number
    botJoinLeadMinutes: number
}

export async function getWorkspaceConfig(): Promise<DsGmeetWorkspaceConfig> {
    const { data } = await oracleServiceAxios.get<Envelope<DsGmeetWorkspaceConfig>>('/api/admin/ds-gmeet/config')
    return data.data
}

export async function updateWorkspaceConfig(patch: Partial<DsGmeetWorkspaceConfig>): Promise<DsGmeetWorkspaceConfig> {
    const { data } = await oracleServiceAxios.put<Envelope<DsGmeetWorkspaceConfig>>('/api/admin/ds-gmeet/config', patch)
    return data.data
}
